import React, { useEffect, useState } from 'react';

import {
  Box, TablePagination, Typography, TextField,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Avatar, Chip, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, InputAdornment
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function PoojaReviews() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = parseInt(searchParams.get('pageSize') || '10', 10);

  const [paginationModel, setPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });

  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [searchKey, setSearchKey] = useState(searchParams.get("searchKey") || "");
  const [ratingFilter, setRatingFilter] = useState(searchParams.get("rating") || "");
  const [startDate, setStartDate] = useState(searchParams.get("startDate") || "");
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || "");
  
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [localSearchKey, setLocalSearchKey] = useState(searchKey);
  const [localRatingFilter, setLocalRatingFilter] = useState(ratingFilter);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);

  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);

  const [actionLoading, setActionLoading] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const fetchReviews = async (page, limit) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      
      const queryParams = new URLSearchParams();
      if (searchKey && searchQuery) {
        queryParams.append(searchKey, searchQuery);
      } else if (searchQuery) {
        queryParams.append('search', searchQuery);
      }
      if (ratingFilter) queryParams.append('rating', ratingFilter);
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);

      const qs = queryParams.toString() ? `&${queryParams.toString()}` : '';

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/review?page=${page + 1}&limit=${limit}${qs}`,
        { headers: { Authorization: token } }
      );
      const result = await response.json();
      if (response.ok) {
        setReviews(result?.data || []);
        setTotalDocs(result?.paginationDetail?.totalDocs || 0);
      } else {
        toast.error('Failed to fetch reviews');
      }
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while fetching reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews(paginationModel.page, paginationModel.pageSize);

    const params = new URLSearchParams();
    params.set('page', String(paginationModel.page));
    params.set('pageSize', String(paginationModel.pageSize));
    if (searchQuery) params.set("search", searchQuery);
    if (searchKey) params.set("searchKey", searchKey);
    if (ratingFilter) params.set("rating", ratingFilter);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, searchKey, ratingFilter, startDate, endDate]);

  const handleFilter = () => {
    setSearchQuery(localSearchQuery);
    setSearchKey(localSearchKey);
    setRatingFilter(localRatingFilter);
    setStartDate(localStartDate);
    setEndDate(localEndDate);
    setPaginationModel({ ...paginationModel, page: 0 });
  };

  const handleResetFilter = () => {
    setLocalSearchQuery('');
    setLocalSearchKey('');
    setLocalRatingFilter('');
    setLocalStartDate('');
    setLocalEndDate('');
    setSearchQuery('');
    setSearchKey('');
    setRatingFilter('');
    setStartDate('');
    setEndDate('');
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handleActionClick = (review, newStatus) => {
    setPendingAction({ id: review.id, newStatus });
    setRejectionReason('');
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { id, newStatus } = pendingAction;

    if (newStatus === 'Rejected' && !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setConfirmOpen(false);
    setActionLoading((prev) => ({ ...prev, [id]: true }));

    try {
      const token = localStorage.getItem("astroVani-admin-token");

      const payload = { moderationStatus: newStatus };
      if (newStatus === 'Rejected') {
        payload.rejectionReason = rejectionReason.trim();
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/review/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast.success(`Review ${newStatus} successfully.`);
        fetchReviews(paginationModel.page, paginationModel.pageSize);
      } else {
        toast.error(result.message || 'Failed to update review status');
      }
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while updating status');
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: false }));
      setPendingAction(null);
    }
  };

  const handleCancelAction = () => {
    setConfirmOpen(false);
    setPendingAction(null);
    setRejectionReason('');
  };

  const handleChangePage = (e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }));
  const handleChangeRowsPerPage = (e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) });

  const columns = [
    { field: 'sno', headerName: 'S.No', width: 65, filterable: false, sortable: false, headerAlign: 'center', align: 'center' },
    {
      field: 'user', headerName: 'User Details', width: 220, headerAlign: 'center', align: 'center',
      renderCell: (params) => {
        const user = params.row.userId;
        if (!user) return '-';
        return (
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Avatar src={user.profileImg || ''} sx={{ width: 30, height: 30 }}>
              {user.fullName ? user.fullName.charAt(0) : 'U'}
            </Avatar> 
            <Box>
              <Typography variant="body2" fontWeight={600}>{user.fullName || '-'}</Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'pooja', headerName: 'Pooja Name', width: 180, headerAlign: 'center', align: 'center',
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1} width="100%">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                overflow: 'hidden',
                flexShrink: 0,
                backgroundColor: '#f3e5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {params.row.poojaId?.image ? (
                <img
                  src={params.row.poojaId.image}
                  alt={params.row.poojaId.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <i className="icofont-om" style={{ fontSize: '20px', color: '#e91e63' }} />
              )}
            </Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.poojaId?.name || '-'}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'rating', headerName: 'Rating', width: 90, headerAlign: 'center', align: 'center',
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} width="100%">
            <Typography variant="body2" fontWeight={600}>{params.value || '0'}</Typography>
            <i className="icofont-star" style={{ color: '#E7B242' }}></i>
          </Box>
        );
      }
    },
    {
      field: 'reviewText', headerName: 'Review', width: 250, headerAlign: 'center', align: 'center',
      renderCell: (params) => {
        return (
          <Typography title={params.value} variant="body2" display="flex" alignItems="center" justifyContent="center" sx={{ whiteSpace: 'normal', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {params.value || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'moderationStatus', headerName: 'Status', width: 130, headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => {
        let color = 'default';
        if (row.moderationStatus === 'Approved') color = 'success';
        if (row.moderationStatus === 'Reject' || row.moderationStatus === 'Rejected') color = 'error';
        if (row.moderationStatus === 'Pending') color = 'warning';
        return <Chip label={row.moderationStatus || 'Pending'} size="small" color={color} sx={{ fontWeight: 600, fontSize: '11px' }} />;
      },
    },
    {
      field: 'actions', headerName: 'Actions', width: 140, headerAlign: 'center', align: 'center', sortable: false, filterable: false,
      renderCell: ({ row }) => {
        if (row.moderationStatus === 'Pending') {
          return (
            <Box display="flex" gap={1} justifyContent="center" alignItems="center" width="100%">
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleActionClick(row, 'Approved'); }}
                  disabled={!!actionLoading[row.id]}
                  sx={{ color: '#388e3c', '&:hover': { backgroundColor: 'rgba(56,142,60,0.08)' } }}
                >
                  <i className="icofont-check-circled" style={{ fontSize: '18px' }}></i>
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleActionClick(row, 'Rejected'); }}
                  disabled={!!actionLoading[row.id]}
                  sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211,47,47,0.08)' } }}
                >
                  <i className="icofont-close-circled" style={{ fontSize: '18px' }}></i>
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        return <Typography variant="body2" color="textSecondary" fontWeight={600}>-</Typography>;
      }
    },
    {
      field: 'createdAt', headerName: 'Date', width: 170, headerAlign: 'center', align: 'center',
      valueFormatter: (value) => value ? new Date(value).toLocaleString() : '-',
    },
  ];

  const rows = reviews.map((item, index) => {
    return {
      sno: paginationModel.page * paginationModel.pageSize + index + 1,
      id: item._id,
      userId: item.userId,
      poojaId: item.poojaId,
      rating: item.rating,
      reviewText: item.reviewText,
      moderationStatus: item.moderationStatus,
      rejectionReason: item.rejectionReason,
      createdAt: item.createdAt ? new Date(item.createdAt) : null,
    };
  });

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Pooja Reviews" />


        {/* Custom Toolbar */}
        <div className="card mt-3 mb-3" style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: 'none'
        }}>
            <div className="card-body" style={{ padding: '16px 20px' }}>
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    '@media (max-width: 768px)': {
                        flexDirection: 'column',
                        alignItems: 'stretch',
                    }
                }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Search Key</InputLabel>
                        <Select
                            value={localSearchKey}
                            label="Search Key"
                            onChange={(e) => setLocalSearchKey(e.target.value)}
                            sx={{ borderRadius: '8px' }}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="reviewText">Review Text</MenuItem>
                            <MenuItem value="poojaName">Pooja Name</MenuItem>
                            <MenuItem value="userName">User Name</MenuItem>
                        </Select>
                    </FormControl>
                    
                    <TextField
                        size="small"
                        placeholder="Search Value..."
                        value={localSearchQuery}
                        onChange={(e) => setLocalSearchQuery(e.target.value)}
                        sx={{
                            minWidth: 200,
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            }
                        }}
                        disabled={!localSearchKey}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <i className="icofont-search" style={{ fontSize: '18px', color: '#666' }}></i>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Rating</InputLabel>
                        <Select
                            value={localRatingFilter}
                            label="Rating"
                            onChange={(e) => setLocalRatingFilter(e.target.value)}
                            sx={{ borderRadius: '8px' }}
                        >
                            <MenuItem value=""><em>All</em></MenuItem>
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="2">2</MenuItem>
                            <MenuItem value="3">3</MenuItem>
                            <MenuItem value="4">4</MenuItem>
                            <MenuItem value="5">5</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="date"
                        label="Start Date"
                        value={localStartDate}
                        onChange={(e) => setLocalStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            },
                            '& .MuiInputBase-input': {
                                padding: '8px 8px 12px',
                                fontSize: '14px',
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '14px',
                            },
                        }}
                    />
                    
                    <TextField
                        size="small"
                        type="date"
                        label="End Date"
                        value={localEndDate}
                        onChange={(e) => setLocalEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: localStartDate }}
                        sx={{
                            minWidth: 150,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                            },
                            '& .MuiInputBase-input': {
                                padding: '8px 8px 12px',
                                fontSize: '14px',
                            },
                            '& .MuiInputLabel-root': {
                                fontSize: '14px',
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleFilter}
                        sx={{
                            backgroundColor: 'var(--primary-color, #E7B242)',
                            '&:hover': {
                                backgroundColor: 'var(--primary-color, #E7B242)',
                                opacity: 0.9,
                            },
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                    >
                        Filter
                    </Button>
                    
                    <Button
                        variant="outlined"
                        onClick={handleResetFilter}
                        sx={{
                            borderColor: 'var(--primary-color, #E7B242)',
                            color: 'var(--primary-color, #E7B242)',
                            '&:hover': {
                                borderColor: 'var(--primary-color, #E7B242)',
                                backgroundColor: 'rgba(231, 178, 66, 0.08)',
                            },
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                    >
                        Reset
                    </Button>
                </Box>
            </div>
        </div>

        <div className="row g-0 mt-3">
          <div className="col-md-12">
            <div className="card" style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}>
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    loading={loading}
                    columns={columns}
                    rows={rows}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    hideFooterPagination
                    autoHeight
                    getRowId={(row) => row.id}
                    onRowClick={(params) => {
                      setViewData(params.row);
                      setViewOpen(true);
                    }}
                    sx={{ 
                      border: 'none',
                      '& .MuiDataGrid-row': { cursor: 'pointer' } 
                    }}
                  />
                </Box>
                <TablePagination
                  component="div"
                  count={totalDocs}
                  page={paginationModel.page}
                  onPageChange={handleChangePage}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={handleCancelAction}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: pendingAction?.newStatus === 'Rejected' ? 2 : 0 }}>
            Are you sure you want to <b>{pendingAction?.newStatus === 'Approved' ? 'Approve' : 'Reject'}</b> this review?
          </DialogContentText>
          {pendingAction?.newStatus === 'Rejected' && (
            <TextField
              autoFocus
              margin="dense"
              label="Rejection Reason"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelAction} variant="outlined" color="secondary" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={pendingAction?.newStatus === 'Approved' ? 'success' : 'error'}
            sx={{ textTransform: 'none', borderRadius: '8px', boxShadow: 'none' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW DETAILS DIALOG */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Review Details</DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f9f9f9', py: 3 }}>
          {viewData && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar src={viewData.userId?.profileImg || ''} sx={{ width: 50, height: 50 }}>
                  {viewData.userId?.fullName ? viewData.userId.fullName.charAt(0) : 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{viewData.userId?.fullName || 'Unknown User'}</Typography>
                  <Typography variant="body2" color="textSecondary">{viewData.poojaId?.name || 'Unknown Pooja'}</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 1, p: 2, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle2" fontWeight={700}>Rating:</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewData.rating || 0}</Typography>
                    <i className="icofont-star" style={{ color: '#E7B242' }}></i>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {viewData.createdAt ? new Date(viewData.createdAt).toLocaleString() : '-'}
                  </Typography>
                </Box>
                
                <Typography variant="subtitle2" fontWeight={700} mt={1}>Review:</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', mb: 2, maxHeight: '300px', overflowY: 'auto' }}>
                  "{viewData.reviewText || 'No review text provided.'}"
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2" fontWeight={700}>Status:</Typography>
                  <Chip 
                    label={viewData.moderationStatus || 'Pending'} 
                    size="small" 
                    color={
                      viewData.moderationStatus === 'Approved' ? 'success' : 
                      viewData.moderationStatus === 'Rejected' || viewData.moderationStatus === 'Reject' ? 'error' : 'warning'
                    } 
                    sx={{ fontWeight: 600 }} 
                  />
                </Box>

                {viewData.moderationStatus === 'Rejected' && viewData.rejectionReason && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" fontWeight={700} color="error.main">Rejection Reason:</Typography>
                    <Typography variant="body2" color="error.main">{viewData.rejectionReason}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewOpen(false)} variant="outlined" sx={{ textTransform: 'none', borderRadius: '8px' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PoojaReviews;
